import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';

interface DueFollowUp {
  id: string;
  title: string;
  due_date: string;
  days_overdue: number;
  referral_lead: {
    first_name: string;
    last_name: string | null;
  };
}

export const useFollowUpNotifications = () => {
  const { user, tenantId } = useAuth();
  const navigate = useNavigate();
  const [dueFollowUps, setDueFollowUps] = useState<DueFollowUp[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (tenantId && user) {
      checkDueFollowUps();
    }
  }, [tenantId, user]);

  const checkDueFollowUps = async () => {
    if (!tenantId) return;

    try {
      setLoading(true);
      
      // Get due follow-ups for this tenant
      const { data, error } = await supabase
        .from('referral_follow_ups')
        .select(`
          id,
          title,
          due_date,
          referral_leads!inner (
            first_name,
            last_name
          )
        `)
        .eq('tenant_id', tenantId)
        .eq('is_completed', false)
        .lte('due_date', new Date().toISOString())
        .order('due_date', { ascending: true });

      if (error) throw error;

      const followUps = (data || []).map(item => {
        const dueDate = new Date(item.due_date);
        const today = new Date();
        const diffTime = today.getTime() - dueDate.getTime();
        const daysDiff = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        return {
          id: item.id,
          title: item.title,
          due_date: item.due_date,
          days_overdue: daysDiff,
          referral_lead: Array.isArray(item.referral_leads) 
            ? item.referral_leads[0] 
            : item.referral_leads
        };
      });

      setDueFollowUps(followUps);

      // Show toast notification for overdue items (only on first load)
      const overdueCount = followUps.filter(f => f.days_overdue > 0).length;
      const todayCount = followUps.filter(f => f.days_overdue === 0).length;

      if (overdueCount > 0) {
        toast.error(
          `${overdueCount} überfällige Wiedervorlage${overdueCount > 1 ? 'n' : ''} in Ihrem CRM`,
          {
            id: 'overdue-follow-ups',
            action: {
              label: 'CRM öffnen',
              onClick: () => navigate('/admin/referral-crm')
            },
            duration: 10000
          }
        );
      } else if (todayCount > 0) {
        toast.info(
          `${todayCount} Wiedervorlage${todayCount > 1 ? 'n' : ''} heute fällig`,
          {
            id: 'today-follow-ups',
            action: {
              label: 'CRM öffnen',
              onClick: () => navigate('/admin/referral-crm')
            },
            duration: 5000
          }
        );
      }

    } catch (error) {
      console.error('Error checking due follow-ups:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsCompleted = async (followUpId: string) => {
    try {
      const { error } = await supabase
        .from('referral_follow_ups')
        .update({ 
          is_completed: true,
          completed_at: new Date().toISOString()
        })
        .eq('id', followUpId);

      if (error) throw error;

      // Remove from local state
      setDueFollowUps(prev => prev.filter(f => f.id !== followUpId));
      
      toast.success('Wiedervorlage als erledigt markiert');
    } catch (error) {
      console.error('Error marking follow-up as completed:', error);
      toast.error('Fehler beim Markieren der Wiedervorlage');
    }
  };

  return {
    dueFollowUps,
    loading,
    checkDueFollowUps,
    markAsCompleted,
    overdueCount: dueFollowUps.filter(f => f.days_overdue > 0).length,
    todayCount: dueFollowUps.filter(f => f.days_overdue === 0).length,
    totalDue: dueFollowUps.length
  };
};